import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DecisionTree } from '../models/decisionTree';
import cloudSyncService, { DecisionRecord } from '../services/cloudSync';
import CloudSyncPanel from './CloudSyncPanel';

const DecisionRecordsView: React.FC = () => {
  const [records, setRecords] = useState<DecisionRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRecords, setFilteredRecords] = useState<DecisionRecord[]>([]);
  const [tags, setTags] = useState<string[]>([]);  // 所有可用标签
  const [selectedTags, setSelectedTags] = useState<string[]>([]);  // 当前选中的标签
  const [sortBy, setSortBy] = useState<'date' | 'question'>('date');  // 排序方式
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');  // 排序方向
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const navigate = useNavigate();

  // 加载决策记录
  useEffect(() => {
    const recordsJson = localStorage.getItem('decisionRecords');
    if (recordsJson) {
      try {
        const loadedRecords = JSON.parse(recordsJson);
        setRecords(loadedRecords);

        // 提取所有唯一标签
        const allTags = new Set<string>();
        loadedRecords.forEach((record: DecisionRecord) => {
          if (record.tags) {
            record.tags.forEach(tag => allTags.add(tag));
          }
        });
        setTags(Array.from(allTags));
      } catch (err) {
        console.error('加载决策记录失败:', err);
      }
    }
  }, []);

  // 排序和过滤记录
  useEffect(() => {
    let filtered = [...records];
    
    // 搜索过滤
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        record.question?.toLowerCase().includes(term) ||
        record.timestamp?.includes(term) ||
        record.decisionTree?.decisions?.some(d => 
          d.title?.toLowerCase().includes(term) || 
          d.description?.toLowerCase().includes(term)
        )
      );
    }
    
    // 标签过滤
    if (selectedTags.length > 0) {
      filtered = filtered.filter(record => 
        record.tags && selectedTags.every(tag => record.tags!.includes(tag))
      );
    }
    
    // 排序
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return sortDir === 'asc' 
          ? new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          : new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      } else {
        return sortDir === 'asc'
          ? a.question.localeCompare(b.question)
          : b.question.localeCompare(a.question);
      }
    });
    
    setFilteredRecords(filtered);
  }, [searchTerm, records, selectedTags, sortBy, sortDir]);

  // 格式化日期
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 查看决策记录
  const viewRecord = (record: DecisionRecord) => {
    // 将决策树存储到会话存储中，以便在查看页面加载
    const treeWithMetadata = {
      ...record.decisionTree,
      recordId: record.id, // 保存记录ID，便于更新
      savedTimestamp: record.timestamp, // 保存原始时间戳，用于上下文
      fromRecord: true // 标记这是从记录中加载的决策树
    };
    sessionStorage.setItem('viewingDecisionTree', JSON.stringify(treeWithMetadata));
    
    // 额外保存决策记录的信息以便后续操作保留上下文
    sessionStorage.setItem('viewingDecisionContext', JSON.stringify({
      recordId: record.id,
      question: record.question,
      timestamp: record.timestamp,
      tags: record.tags || []
    }));
    
    navigate('/view-decision');
  };

  // 删除决策记录
  const deleteRecord = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('确定要删除这条决策记录吗？')) {
      const updatedRecords = records.filter(r => r.id !== id);
      setRecords(updatedRecords);
      localStorage.setItem('decisionRecords', JSON.stringify(updatedRecords));
    }
  };

  // 添加或移除标签
  const toggleTag = (recordId: string, tag: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const updatedRecords = records.map(record => {
      if (record.id === recordId) {
        const currentTags = record.tags || [];
        let newTags;
        
        if (currentTags.includes(tag)) {
          // 移除标签
          newTags = currentTags.filter(t => t !== tag);
        } else {
          // 添加标签
          newTags = [...currentTags, tag];
        }
        
        return { ...record, tags: newTags };
      }
      return record;
    });
    
    setRecords(updatedRecords);
    localStorage.setItem('decisionRecords', JSON.stringify(updatedRecords));
    
    // 更新所有可用标签
    const allTags = new Set<string>();
    updatedRecords.forEach((record: DecisionRecord) => {
      if (record.tags) {
        record.tags.forEach(tag => allTags.add(tag));
      }
    });
    setTags(Array.from(allTags));
  };

  // 添加新标签
  const addNewTag = (recordId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newTag = prompt('请输入新标签：');
    if (newTag && newTag.trim()) {
      toggleTag(recordId, newTag.trim(), event);
    }
  };

  // 改变排序方式
  const handleSortChange = (sortField: 'date' | 'question') => {
    if (sortBy === sortField) {
      // 如果是同一个字段，切换排序方向
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // 如果是新字段，设置为默认降序
      setSortBy(sortField);
      setSortDir('desc');
    }
  };

  // 切换选中标签
  const handleTagSelect = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) 
        : [...prev, tag]
    );
  };

  // 执行云同步
  const handleSync = async () => {
    if (!cloudSyncService.isSyncEnabled()) {
      setSyncStatus('error');
      setSyncMessage('请先配置云同步');
      return;
    }
    
    setSyncStatus('syncing');
    setSyncMessage('正在同步数据...');
    
    try {
      const result = await cloudSyncService.syncRecords();
      
      if (result.success) {
        setSyncStatus('success');
        setSyncMessage(`同步成功：上传了 ${result.uploadedCount} 条记录，下载了 ${result.downloadedCount} 条记录`);
        
        // 重新加载记录以显示同步的数据
        const recordsJson = localStorage.getItem('decisionRecords');
        if (recordsJson) {
          const loadedRecords = JSON.parse(recordsJson);
          setRecords(loadedRecords);
          
          // 重新提取标签
          const allTags = new Set<string>();
          loadedRecords.forEach((record: DecisionRecord) => {
            if (record.tags) {
              record.tags.forEach(tag => allTags.add(tag));
            }
          });
          setTags(Array.from(allTags));
        }
      } else {
        setSyncStatus('error');
        setSyncMessage(`同步失败：${result.message}`);
      }
    } catch (error) {
      setSyncStatus('error');
      setSyncMessage(`同步出错：${error instanceof Error ? error.message : String(error)}`);
    }
    
    // 5秒后清除状态消息
    setTimeout(() => {
      setSyncStatus('idle');
      setSyncMessage('');
    }, 5000);
  };

  // 显示同步状态指示器
  const renderSyncStatus = () => {
    if (syncStatus === 'idle' && !syncMessage) return null;
    
    const statusClasses = {
      idle: 'bg-gray-100 text-gray-800',
      syncing: 'bg-blue-100 text-blue-800 animate-pulse',
      success: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800'
    };
    
    return (
      <div className={`mb-4 p-3 rounded-md border ${statusClasses[syncStatus]}`}>
        {syncStatus === 'syncing' && (
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {syncMessage}
          </div>
        )}
        {syncStatus !== 'syncing' && syncMessage}
      </div>
    );
  };

  // 切换比较模式
  const toggleCompareMode = () => {
    setCompareMode(!compareMode);
    // 退出比较模式时清空选择
    if (compareMode) {
      setSelectedForCompare([]);
    }
  };

  // 选择/取消选择记录
  const toggleSelectForCompare = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedForCompare(prev => 
      prev.includes(id) 
        ? prev.filter(recordId => recordId !== id) 
        : [...prev, id]
    );
  };

  // 比较已选记录
  const compareSelectedRecords = () => {
    if (selectedForCompare.length < 2) {
      alert('请至少选择2个决策记录进行比较');
      return;
    }
    
    if (selectedForCompare.length > 3) {
      alert('最多只能比较3个决策记录');
      return;
    }
    
    // 跳转到比较页面，并传递选中记录的ID
    navigate(`/compare?ids=${selectedForCompare.join(',')}`);
  };

  return (
    <div className="w-full max-w-4xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">决策记录</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/')} 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            返回首页
          </button>
          <button
            onClick={() => {
              if (window.confirm('确定要清空所有决策记录吗？此操作不可撤销。')) {
                localStorage.removeItem('decisionRecords');
                setRecords([]);
                setFilteredRecords([]);
                setTags([]);
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
          >
            清空记录
          </button>
        </div>
      </div>

      {/* 显示同步状态 */}
      {renderSyncStatus()}

      <div className="mb-6">
        <div className="relative rounded-md shadow-sm">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pr-10 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="搜索决策问题、内容或日期..."
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* 标签过滤 */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">标签过滤:</span>
          {tags.length > 0 ? (
            tags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagSelect(tag)}
                className={`px-2 py-1 text-xs rounded-full ${
                  selectedTags.includes(tag)
                    ? 'bg-blue-100 text-blue-800 border border-blue-300'
                    : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))
          ) : (
            <span className="text-sm text-gray-500">无可用标签</span>
          )}
          
          {selectedTags.length > 0 && (
            <button
              onClick={() => setSelectedTags([])}
              className="ml-2 text-xs text-gray-500 hover:text-gray-700"
            >
              清除筛选
            </button>
          )}
        </div>
      </div>

      {/* 比较功能 */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="搜索决策记录..."
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          
          <div className="relative">
            <button
              onClick={() => setSelectedTags([])}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 focus:outline-none flex items-center"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              标签 {selectedTags.length > 0 && `(${selectedTags.length})`}
            </button>
            
            {selectedTags.length > 0 && (
              <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="p-2">
                  {tags.length > 0 ? (
                    <div className="space-y-1">
                      {tags.map(tag => (
                        <div 
                          key={tag} 
                          className="flex items-center px-2 py-1 hover:bg-gray-100 rounded-md cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTagSelect(tag);
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTags.includes(tag)}
                            readOnly
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{tag}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 p-2">没有可用标签</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {compareMode ? (
            <>
              <button
                onClick={compareSelectedRecords}
                disabled={selectedForCompare.length < 2 || selectedForCompare.length > 3}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  selectedForCompare.length >= 2 && selectedForCompare.length <= 3
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                } transition-colors`}
              >
                比较已选 ({selectedForCompare.length})
              </button>
              <button
                onClick={toggleCompareMode}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
              >
                取消
              </button>
            </>
          ) : (
            <button
              onClick={toggleCompareMode}
              className="px-4 py-2 border border-indigo-500 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              选择并比较
            </button>
          )}
        </div>
      </div>

      {filteredRecords.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          {searchTerm || selectedTags.length > 0 ? '未找到匹配的决策记录' : '暂无保存的决策记录'}
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredRecords.map((record) => (
              <li 
                key={record.id}
                className={`mb-4 p-4 bg-white shadow-sm rounded-lg border ${
                  compareMode && selectedForCompare.includes(record.id) 
                    ? 'border-indigo-500 ring-2 ring-indigo-200' 
                    : 'border-gray-200 hover:border-gray-300'
                } transition-colors cursor-pointer relative`}
                onClick={(e) => compareMode ? toggleSelectForCompare(record.id, e as any) : viewRecord(record)}
              >
                {compareMode && (
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                    <input
                      type="checkbox"
                      checked={selectedForCompare.includes(record.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelectForCompare(record.id, e as any);
                      }}
                      className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                  </div>
                )}
                
                <div className={`${compareMode ? 'pl-10' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-md font-medium text-gray-900 truncate pr-2">
                      {record?.decisionTree?.question || "未命名决策"}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {(record?.tags || []).slice(0, 3).map((tag, idx) => (
                        <span 
                          key={idx} 
                          className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTagSelect(tag);
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {record?.decisionTree?.decisions && record.decisionTree.decisions.length > 0 
                      ? record.decisionTree.decisions[0].description 
                      : "无决策数据"}
                  </p>
                  
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>
                      {new Date(record.timestamp).toLocaleString()} · 
                      {record?.decisionTree?.decisions?.length || 0}个决策选项
                    </span>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => deleteRecord(record.id, e)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">共 {filteredRecords.length} 条记录{records.length !== filteredRecords.length ? `（总共 ${records.length} 条）` : ''}</p>
      </div>
    </div>
  );
};

export default DecisionRecordsView; 